import { AttendeesList, IEventrActivity, IOptionsWithTypes, ITodoTask, parseTodoLabel } from "@eventr/common";

type RegularTodoItem = { type: "regular"; item: ITodoTask };
type ParentInlineTodoItem = { type: "parent-inline"; parent: ITodoTask; children: ITodoTask[] };
export type GroupedTodoItem = RegularTodoItem | ParentInlineTodoItem;

/**
 * Group a regular "parent" item with its consecutive dot-prefixed children.
 * e.g. "Monday Breakfast", ".Lunch", ".Dinner" → one row.
 * Items without dot prefix and not preceded by a parent are rendered individually.
 */
export function groupTodoTasksByInline(tasks: ITodoTask[]): GroupedTodoItem[] {
  const result: GroupedTodoItem[] = [];
  let parent: ITodoTask | null = null;
  let children: ITodoTask[] = [];

  for (const task of tasks) {
    const { inline } = parseTodoLabel(task.label);

    if (inline) {
      // Dot-prefixed item: add as child of the current parent (if any)
      if (parent) {
        children.push(task);
      } else {
        // Orphan inline item with no parent — treat as regular
        result.push({ type: "regular", item: task });
      }
    } else {
      // Non-dot item: flush previous parent+children group first
      if (parent) {
        result.push({ type: "parent-inline", parent, children });
        parent = null;
        children = [];
      }
      // Start a new potential parent
      parent = task;
    }
  }

  // Flush any remaining parent+children at the end
  if (parent) {
    result.push({ type: "parent-inline", parent, children });
  }

  return result;
}

export const getAllAttendees = (activity: IEventrActivity): IOptionsWithTypes[] => {
  const attendees = activity?.attendees_list;
  if (!attendees) return [];
  const attendeesArr = Array.isArray(attendees) ? attendees : Object.values(attendees);

  return attendeesArr.flatMap((a: any) => a?.users ?? []);
};

// Built once: an imported exhibitor list runs this comparator ~n log n times (40k+ for IMEX), and
// constructing a collator per call — which is what `localeCompare` does — dominated the sort.
// `sensitivity: "base"` already folds case and accents, so the `.toLowerCase()` calls are redundant.
const standNameCollator = new Intl.Collator(undefined, { sensitivity: "base" });

export const sortStands = (stands: IEventrActivity[]) => {
  stands.sort((a: IEventrActivity, b: IEventrActivity) => {
    const nameA = a["activity_name"];
    const nameB = b["activity_name"];

    if (nameA == null && nameB == null) return 0;

    if (nameA == null) return 1;

    if (nameB == null) return -1;

    return standNameCollator.compare(nameA, nameB);
  });
};

export const currentTodoGroup = (todoList: AttendeesList[], wpUserId: number | undefined) => {
  const attendee = todoList?.find?.((group) => {
    return group.users?.some((user) => user.option === wpUserId);
  });
  return attendee?._id;
};

export const todoStatusForCurrentGroup = (todoStatuses: any, currentUserGroup: string | undefined) => {
  if (!todoStatuses || typeof todoStatuses !== "object") return {};

  return Object.entries(todoStatuses).reduce<Record<string, any>>((acc, [taskId, todoFillers]) => {
    const entries = Array.isArray(todoFillers) ? todoFillers : todoFillers ? [todoFillers] : [];
    const currentGroupEntry = entries.find((entry) => entry?.value?.group_id === currentUserGroup);
    /** This fallback entry is for when the group_ids do not exist, this is
     *  usually the case with the old way of doing todos. */
    const fallbackEntry = entries.find((entry) => !entry?.value?.group_id);

    if (currentGroupEntry) {
      acc[taskId] = currentGroupEntry;
    } else if (fallbackEntry) {
      acc[taskId] = fallbackEntry;
    }

    return acc;
  }, {});
};
